import { Box, Typography } from '@material-ui/core';
import noTokenIcon from './assets/no-token.png';
import createdTokenIcon from './assets/created-token.png';
import noAccessTokenIcon from './assets/no-access-token.png';
import expiredTokenIcon from './assets/expired-token.png';
import { useTruncatedStyles, WbButton } from '@agilelab/plugin-wb-platform';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { makeStyles } from '@material-ui/core/styles';
import { useState, useMemo } from 'react';
import { AccessToken, SafeAccessToken } from '@agilelab/plugin-wb-auth-backend';

const useStyles = makeStyles(theme => ({
  containerBase: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    borderRadius: 4,
    boxShadow: theme.shadows[2],
    border: `1px solid ${theme.palette.grey[300]}`,
    backgroundColor: theme.palette.grey[50],
    [theme.breakpoints.down('xs')]: {
      gap: 0,
    },
  },
  containerGenerated: {
    backgroundColor: theme.palette.bkg.success,
    border: `1px solid ${theme.palette.success.light}`,
  },
  containerExpired: {
    border: `1px solid ${theme.palette.error.light}`,
  },
  illustration: {
    width: '100%',
    maxWidth: 128,
    [theme.breakpoints.down('xs')]: {
      maxWidth: 96,
    },
  },
  title: {
    fontWeight: 500,
  },
  subtitle: {
    marginTop: 4,
  },
  actionsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
  },
  maskedTokenBox: {
    flex: '1 1 auto',
    fontFamily: 'monospace',
    fontSize: '14px',
    color: '#666',
    letterSpacing: '2px',
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 4,
    padding: '4px 12px',
    backgroundColor: theme.palette.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
}));

interface AccessTokenCardProps {
  accessTokens: SafeAccessToken[] | undefined;
  generatedToken: AccessToken | null;
  isUser?: boolean;
}

export enum TokenStatus {
  NOT_GENERATED,
  GENERATED,
  COPIED,
  EXPIRED,
}

export const AccessTokenStatus = ({
  accessTokens,
  generatedToken,
  isUser = false,
}: AccessTokenCardProps) => {
  const classes = useStyles();
  const { truncated } = useTruncatedStyles({ maxLines: 1, breakAll: true });

  const hasAnyExpired = useMemo(() => {
    if (!accessTokens?.length) return false;
    return accessTokens.some(
      token =>
        token.expires_at && new Date(token.expires_at).getTime() <= Date.now(),
    );
  }, [accessTokens]);

  const tokenStatus = useMemo(() => {
    if (!accessTokens || !accessTokens.length) {
      return TokenStatus.NOT_GENERATED;
    }

    if (generatedToken) {
      return TokenStatus.GENERATED;
    }

    if (hasAnyExpired) {
      return TokenStatus.EXPIRED;
    }

    return TokenStatus.COPIED;
  }, [accessTokens, generatedToken, hasAnyExpired]);

  const [showToken, setShowToken] = useState(false);

  const accessTokenValue = generatedToken?.access_token ?? null;

  const displayedToken = useMemo(() => {
    if (!accessTokenValue) return '';
    return showToken ? accessTokenValue : '•'.repeat(accessTokenValue.length);
  }, [accessTokenValue, showToken]);

  const handleCopyToken = () => {
    if (!accessTokenValue) return;
    navigator.clipboard.writeText(accessTokenValue);
  };

  switch (tokenStatus) {
    case TokenStatus.NOT_GENERATED:
      return (
        <Box className={classes.containerBase}>
          <img src={noTokenIcon} alt="" className={classes.illustration} />
          <Box>
            <Typography variant="h6" className={classes.title}>
              No access token has been generated yet.
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              className={classes.subtitle}
            >
              {isUser
                ? 'Create an access token to grant access to your account.'
                : 'Create an access token to grant access to this service account.'}
            </Typography>
          </Box>
        </Box>
      );
    case TokenStatus.GENERATED:
      return (
        <Box
          className={`${classes.containerBase} ${classes.containerGenerated}`}
        >
          <img src={createdTokenIcon} alt="" className={classes.illustration} />
          <Box style={{ width: '100%' }}>
            <Typography variant="h6" className={classes.title}>
              A new access token has been created.
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              className={classes.subtitle}
            >
              Copy and save the generated token, as it will no longer be
              accessible.
            </Typography>
            <Box className={classes.actionsRow}>
              <Box className={classes.maskedTokenBox}>
                <Typography className={truncated} variant="body2">
                  {displayedToken}
                </Typography>
                <WbButton
                  aria-label={showToken ? 'hide token' : 'show token'}
                  onClick={() => setShowToken(!showToken)}
                  size="small"
                  variant="text"
                  color="default"
                  icon={showToken ? <VisibilityOffIcon /> : <VisibilityIcon />}
                />
              </Box>
              <WbButton
                onClick={handleCopyToken}
                variant="outlined"
                color="secondary"
                label="Copy"
                startIcon={<FileCopyIcon />}
                size="small"
              />
            </Box>
          </Box>
        </Box>
      );

    case TokenStatus.COPIED:
      return (
        <Box className={classes.containerBase}>
          <img
            src={noAccessTokenIcon}
            alt=""
            className={classes.illustration}
          />
          <Box>
            <Typography variant="h6" className={classes.title}>
              This account has an active token
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              className={classes.subtitle}
            >
              The token value was only shown when it was first created and can't
              be viewed again. If you've lost it or it's been tampered with, you
              can regenerate the token while keeping the same expiration date,
              or delete it and create a new one.
            </Typography>
          </Box>
        </Box>
      );
    case TokenStatus.EXPIRED:
      return (
        <Box className={`${classes.containerBase} ${classes.containerExpired}`}>
          <img src={expiredTokenIcon} alt="" className={classes.illustration} />
          <Box>
            <Typography variant="h6" className={classes.title}>
              The access token has expired
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              className={classes.subtitle}
            >
              The access token has expired and is no longer valid. Create a new
              one.
            </Typography>
          </Box>
        </Box>
      );
    default:
      return null;
  }
};
